import { memo, useEffect, useRef } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Image,
  MediaToggle,
  Spinner,
  Text,
  tokenPrefix,
} from '@hivewatch/dark'

import { ClipLoaderEvents, trackEvent } from '@/utils/analytics'

import { MixpanelDataIProps } from '../../../shared/types/types'
import { ViewToolbar, ViewToolbarView } from './ViewToolbar'

export interface ClipLoaderIProps {
  deviceName: string
  hasVideoClip: boolean
  hasTimedOut: boolean
  incidentId: string
  mixpanelData: MixpanelDataIProps
  onClipClick: () => void
  onLiveClick: () => void
  /** Called when ClipLoader is first shown, for parent to track state */
  onShown?: () => void
  previewImageUrl?: string | null
  /** Age of the source clip behind the preview still, in seconds */
  previewClipAgeSeconds?: number | null
  /** How long the preview still took to load, in ms */
  previewLoadDurationMs?: number | null
  /**
   * When true, dock the view-toolbar (Live / Focused Clip / Extended Clip) above the loading
   * area instead of the in-loader Live/Clip toggle — the same slot the toolbar occupies above
   * the player, so it doesn't move across the loading → player transition. Additionally requires
   * `FeatureFlag.resolvedIncidentClip`, like the player.
   */
  useViewToolbar?: boolean
}

const ClipLoaderComponent = ({
  deviceName,
  hasVideoClip,
  hasTimedOut,
  incidentId,
  mixpanelData,
  onClipClick,
  onLiveClick,
  onShown,
  previewImageUrl,
  previewClipAgeSeconds,
  previewLoadDurationMs,
  useViewToolbar = false,
}: ClipLoaderIProps) => {
  const mode = useLightMode()
  const prefix = tokenPrefix(mode)
  const { t } = useTranslation()
  const isResolvedClipEnabled = useFeatureFlag(FeatureFlag.resolvedIncidentClip)
  const shouldShowViewToolbar = useViewToolbar && !!isResolvedClipEnabled
  const hasTrackedShown = useRef(false)
  const hasTrackedPreviewReady = useRef(false)
  const prevIncidentIdRef = useRef(incidentId)

  // Reset tracking when incident changes
  if (prevIncidentIdRef.current !== incidentId) {
    prevIncidentIdRef.current = incidentId
    hasTrackedShown.current = false
    hasTrackedPreviewReady.current = false
  }

  // Track "Shown" on mount. This is the pre-existing clip-loader metric and is
  // intentionally independent of the blurred preview — the preview's age/load
  // time are tracked on `showedBlurredPreview` when the still actually renders.
  useEffect(() => {
    if (!hasTrackedShown.current) {
      hasTrackedShown.current = true
      trackEvent(ClipLoaderEvents.shown, mixpanelData)
      onShown?.()
    }
    // incidentId re-runs this after the render-time ref reset even when
    // mixpanelData/onShown are referentially stable across incident changes.
  }, [incidentId, mixpanelData, onShown])

  // Track when the blurred preview still is ready, along with how long it took
  // to load. Fires separately from "Shown" as that's the thin image-less preview
  // that's shown if this feature flag doesn't exist and/or if we can't resolve to a clip
  useEffect(() => {
    if (
      !hasTrackedPreviewReady.current &&
      previewImageUrl &&
      previewLoadDurationMs != null
    ) {
      hasTrackedPreviewReady.current = true
      trackEvent(ClipLoaderEvents.showedBlurredPreview, {
        ...mixpanelData,
        preview_load_duration_ms: previewLoadDurationMs,
        blurred_clip_preview_age_seconds: previewClipAgeSeconds,
      })
    }
  }, [
    incidentId,
    previewImageUrl,
    previewLoadDurationMs,
    previewClipAgeSeconds,
    mixpanelData,
  ])

  // Show loading spinner only if clip hasn't arrived AND timeout hasn't expired
  const isClipLoading = !hasVideoClip && !hasTimedOut

  const handleSelectView = (view: ViewToolbarView) => {
    if (view === 'live') {
      onLiveClick()
    } else if (view === 'focused') {
      onClipClick()
    }
    // 'extended' is never selectable here — the tab stays loading/disabled until the player
    // takes over.
  }

  // Docked above the loading area — the focused clip is the view being prepared, so it reads as
  // the (loading) selection while the Live tab stays clickable.
  const viewToolbar = shouldShowViewToolbar && (
    <ViewToolbar
      activeView='focused'
      extendedState={isClipLoading ? 'loading' : 'disabled'}
      focusedState={isClipLoading ? 'loading' : 'ready'}
      onSelect={handleSelectView}
    />
  )

  if (previewImageUrl) {
    return (
      <Box pt='20px' px='24px'>
        {viewToolbar}
        <Box
          borderRadius='8px'
          data-testid='clip-loader'
          h='320px'
          overflow='hidden'
          position='relative'
        >
          {/* Blurred preview background */}
          <Image
            alt=''
            filter='blur(5px)'
            h='100%'
            objectFit='cover'
            position='absolute'
            src={previewImageUrl}
            transform='scale(1.05)'
            w='100%'
          />
          {/* Dark overlay */}
          <Box
            bg={`${prefix}bg.overlay`}
            h='100%'
            left='0'
            position='absolute'
            top='0'
            w='100%'
          />
          {/* Centered loading indicator */}
          <Box
            alignItems='center'
            bottom='0'
            display='flex'
            flexDirection='column'
            gap='12px'
            justifyContent='center'
            left='0'
            position='absolute'
            right='0'
            top='0'
          >
            <Spinner
              borderWidth='3px'
              color={`${prefix}text.on-overlay`}
              size='lg'
              speed='1.2s'
            />
            <Text
              color={`${prefix}text.on-overlay`}
              fontSize='13px'
              fontWeight='medium'
            >
              {t('incidents:clip_loader_connecting_to_camera')}
            </Text>
          </Box>
          {/* Camera name + toggle — bottom left */}
          <Box bottom='10px' left='12px' position='absolute'>
            <Text
              color={`${prefix}text.on-overlay`}
              fontSize='12px'
              fontWeight='medium'
              letterSpacing='0.04em'
              lineClamp={1}
              mb='6px'
            >
              {deviceName || t('incidents:clip_loader_camera_fallback')}
            </Text>
            {!shouldShowViewToolbar && (
              <MediaToggle
                clipLabel={t('incidents:clip_loader_clip_available_label')}
                colorScheme={mode}
                isClipLoading={isClipLoading}
                liveLabel={t('incidents:clip_loader_live_label')}
                onClipClick={onClipClick}
                onLiveClick={onLiveClick}
                retrievingLabel={t(
                  'incidents:clip_loader_retrieving_clip_label'
                )}
              />
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box pt='20px' px='24px'>
      {viewToolbar}
      <Box
        alignItems='center'
        bg={`${prefix}bg.card-elevated`}
        border='1px solid'
        borderColor={`${prefix}border.default`}
        borderRadius='8px'
        data-testid='clip-loader'
        display='flex'
        overflow='hidden'
        pr='8px'
      >
        <Box flex='1' minW='0' px='14px' py='10px'>
          <Text
            color={`${prefix}text.secondary`}
            fontSize='12px'
            fontWeight='medium'
            letterSpacing='0.04em'
            overflow='hidden'
            textOverflow='ellipsis'
            whiteSpace='nowrap'
          >
            {deviceName || t('incidents:clip_loader_camera_fallback')}
          </Text>
        </Box>

        {!shouldShowViewToolbar && (
          <MediaToggle
            clipLabel={t('incidents:clip_loader_clip_available_label')}
            colorScheme={mode}
            isClipLoading={isClipLoading}
            liveLabel={t('incidents:clip_loader_live_label')}
            onClipClick={onClipClick}
            onLiveClick={onLiveClick}
            retrievingLabel={t('incidents:clip_loader_retrieving_clip_label')}
          />
        )}
      </Box>
    </Box>
  )
}

const arePropsEqual = (prev: ClipLoaderIProps, next: ClipLoaderIProps) =>
  prev.deviceName === next.deviceName &&
  prev.hasVideoClip === next.hasVideoClip &&
  prev.hasTimedOut === next.hasTimedOut &&
  prev.incidentId === next.incidentId &&
  prev.mixpanelData === next.mixpanelData &&
  prev.previewImageUrl === next.previewImageUrl &&
  prev.previewClipAgeSeconds === next.previewClipAgeSeconds &&
  prev.previewLoadDurationMs === next.previewLoadDurationMs &&
  prev.useViewToolbar === next.useViewToolbar

export const ClipLoader = memo(ClipLoaderComponent, arePropsEqual)
